import { useCallback } from 'react';
import classNames from './MoveStudentsPopup.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { StudentReportItem } from '@/school-management/types/response';
import { formatPersonName } from '@/utils/personName';
import {
  MoveClassDestination,
  BulkOperationResponseData,
  MoveStudentsBulkOperationItem,
  BulkOperationState,
} from '@/school-management/types/services/bulk';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import processing from '@/school-management/utils/processing';

interface DownloadProps {
  students: StudentReportItem[];
  classDestination: MoveClassDestination;
  moveSource: 'ChooseDestination' | 'DifferentGradeAll' | 'DifferentGradeSame';
  bulkOperation?: BulkOperationResponseData;
  bulkOperationState:
    | BulkOperationState.Completed
    | BulkOperationState.PartialSuccess
    | BulkOperationState.Failed;
}

const Download = ({
  students,
  classDestination,
  moveSource,
  bulkOperation,
  bulkOperationState,
}: DownloadProps) => {
  const { t } = useAdminTranslation();

  const download = useCallback(() => {
    let studentsToProcess = students || [];

    if (moveSource === 'DifferentGradeSame') {
      studentsToProcess = studentsToProcess.filter(s => s.gradeId === classDestination.gradeId);
    }

    const bulkStudentsMapper = (
      (bulkOperation?.results || []) as MoveStudentsBulkOperationItem[]
    ).reduce(
      (acc, s) => {
        if (s.studentId) {
          acc[s.studentId] = s;
        }

        return acc;
      },
      {} as {
        [studentId: string]: MoveStudentsBulkOperationItem;
      }
    );

    const tableName = t('bulkActions.moveStudents.report.fileName');
    const keys = {
      name: t('bulkActions.common.name'),
      moveFromTeacher: t('bulkActions.moveStudents.report.moveFromTeacher'),
      moveToTeacher: t('bulkActions.moveStudents.report.moveToTeacher'),
      moveFromClass: t('bulkActions.moveStudents.report.moveFromClass'),
      moveToClass: t('bulkActions.moveStudents.report.moveToClass'),
      moveFromGrade: t('bulkActions.moveStudents.report.moveFromGrade'),
      moveToGrade: t('bulkActions.moveStudents.report.moveToGrade'),
      action: t('bulkActions.common.action'),
      actionMove: t('bulkActions.moveStudents.report.actionMove'),
      status: t('status'),
      statusSuccess: t('bulkActions.moveStudents.report.statusSuccess'),
      statusFailure: t('bulkActions.moveStudents.report.statusFailure'),
    };
    const headerRow: string[] = [
      keys.name,
      keys.moveFromTeacher,
      keys.moveFromClass,
      keys.moveFromGrade,
      keys.moveToTeacher,
      keys.moveToClass,
      keys.moveToGrade,
      keys.action,
      keys.status,
    ];
    const data: {
      [key: string]: any;
    }[] = studentsToProcess.map(s => {
      const isSuccess =
        bulkOperationState === BulkOperationState.Completed
          ? true
          : bulkOperationState === BulkOperationState.Failed
            ? false
            : s.userId in bulkStudentsMapper
              ? bulkStudentsMapper[s.userId].success
              : true;
      const error =
        bulkOperationState === BulkOperationState.Completed
          ? ''
          : s.userId in bulkStudentsMapper
            ? bulkStudentsMapper[s.userId].error
            : bulkOperationState === BulkOperationState.Failed
              ? 'Unexpected error'
              : '';

      return {
        [keys.name]: s.name,
        [keys.moveFromTeacher]: (s.teachers || [])
          .map(t => formatPersonName(t.firstName, t.lastName))
          .join(', '),
        [keys.moveFromClass]: s.groupName,
        [keys.moveFromGrade]: `${t(`grade_${s.gradeId}`)} ${t('grade')}`,
        [keys.moveToTeacher]: (classDestination?.teacherNames || []).join(', '),
        [keys.moveToClass]: classDestination?.groupName || '',
        [keys.moveToGrade]: classDestination?.gradeId
          ? `${t(`grade_${classDestination.gradeId}`)} ${t('grade')}`
          : '',
        [keys.action]: keys.actionMove,
        [keys.status]: isSuccess ? keys.statusSuccess : keys.statusFailure,
      };
    });

    processing.createXlsx({
      fileName: tableName,
      tables: [
        {
          name: tableName,
          headerRow: headerRow,
          data: data,
        },
      ],
    });
  }, [students, classDestination, moveSource, bulkOperation, bulkOperationState, t]);

  return (
    <div className={classNames.download}>
      <button onClick={download}>
        <AppIcon icon='download' width={10} height={15} />
        <span>{t('bulkActions.downloadChanges.downloadButton')}</span>
      </button>
      <div>{t('bulkActions.downloadChanges.text')}</div>
    </div>
  );
};

export default Download;
